import React, { useMemo } from 'react';
import { Card, Tag } from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';

const ImportReviewStep = ({ accountData, selectedFields, onImport, isDisabled }) => {
    const getSelectedCount = (field) => {
        if (!selectedFields?.[field]) return 0;

        if (typeof selectedFields[field] === 'boolean') {
            return selectedFields[field] ? 1 : 0;
        }

        if (typeof selectedFields[field] === 'object') {
            return Object.values(selectedFields[field]).filter(Boolean).length;
        }

        return 0;
    };

    const categories = useMemo(() => {
        return [
            {
                name: 'Info',
                icon: 'fas fa-info-circle',
                fields: [
                    { key: 'about', label: 'About' },
                    { key: 'profile_links', label: 'Profile Links' },
                    { key: 'locations', label: 'Locations' }
                ]
            },
            {
                name: 'Brands',
                icon: 'fas fa-handshake',
                fields: [{ key: 'brands', label: 'Brands' }]
            },
            {
                name: 'Work Gallery',
                icon: 'fas fa-images',
                fields: [{ key: 'assets', label: 'Assets' }]
            },
            {
                name: 'Project Highlights',
                icon: 'fas fa-briefcase',
                fields: [{ key: 'key_clients', label: 'Key Clients' }]
            },
            {
                name: 'Partner Programs',
                icon: 'fas fa-certificate',
                fields: [
                    { key: 'partner_programs', label: 'Partner Programs' },
                    { key: 'partnerable_programs', label: 'Partnerable Programs' }
                ]
            },
            {
                name: 'Industries',
                icon: 'fas fa-building',
                fields: [{ key: 'industries_worked', label: 'Industries' }]
            },
            {
                name: 'Awards & Certifications',
                icon: 'fas fa-trophy',
                fields: [
                    { key: 'awards', label: 'Awards' },
                    { key: 'certifications', label: 'Certifications' }
                ]
            },
            {
                name: 'Service Offerings',
                icon: 'fas fa-cogs',
                fields: [{ key: 'service_offerings', label: 'Service Offerings' }]
            }
        ].filter(category => {
            // Only include categories that have data
            return category.fields.some(field => {
                const data = accountData?.[field.key];
                return data && (Array.isArray(data) ? data.length > 0 : true);
            });
        });
    }, [accountData]);

    const totalSelectedCount = useMemo(() => {
        let total = 0;
        categories.forEach(category => {
            category.fields.forEach(field => {
                total += getSelectedCount(field.key);
            });
        });
        return total;
    }, [categories, selectedFields]);

    return (
        <div className="py-6">
            <h2 className="text-2xl font-semibold mb-2">Review Your Selections</h2>
            <p className="text-gray-600 mb-6">
                Review the items you've selected to import. You can go back to any step to make changes.
            </p>

            {/* Summary Card */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6 flex items-start gap-3">
                <CheckCircleFilled className="text-purple-600 text-xl mt-0.5" />
                <div>
                    <h3 className="font-semibold text-purple-900 mb-1 mt-0">Ready to Import</h3>
                    <p className="text-purple-800 m-0">
                        You've selected <strong>{totalSelectedCount}</strong> item{totalSelectedCount !== 1 ? 's' : ''} to import across <strong>{categories.length}</strong> categor{categories.length !== 1 ? 'ies' : 'y'}.
                    </p>
                </div>
            </div>

            {/* Categories Summary */}
            <div className="space-y-4">
                {categories.map((category, index) => {
                    const categoryTotal = category.fields.reduce((sum, field) => {
                        return sum + getSelectedCount(field.key);
                    }, 0);

                    if (categoryTotal === 0) return null;

                    return (
                        <Card key={index}>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                                        <i className={`${category.icon} text-purple-600`} />
                                    </div>
                                    <div>
                                        <p className="font-semibold m-0">{category.name}</p>
                                        <p className="text-sm text-gray-600 m-0">
                                            {category.fields.map((field, idx) => {
                                                const count = getSelectedCount(field.key);
                                                if (count === 0) return null;
                                                return (
                                                    <span key={idx}>
                                                        {idx > 0 && category.fields.slice(0, idx).some(f => getSelectedCount(f.key) > 0) && ', '}
                                                        {count} {field.label.toLowerCase()}
                                                    </span>
                                                );
                                            }).filter(Boolean)}
                                        </p>
                                    </div>
                                </div>
                                <Tag color="purple" className="m-0">
                                    {categoryTotal} selected
                                </Tag>
                            </div>
                        </Card>
                    );
                })}
            </div>

            {totalSelectedCount === 0 && (
                <Card>
                    <div className="text-center py-8">
                        <p className="text-gray-500 m-0">No items selected for import.</p>
                        <p className="text-gray-500 text-sm m-0 mt-2">Go back to previous steps to select items.</p>
                    </div>
                </Card>
            )}

            {/* Important Notice */}
            {totalSelectedCount > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
                    <h4 className="font-semibold text-blue-900 mb-2 mt-0">
                        <i className="fas fa-info-circle mr-2" />
                        What Happens Next?
                    </h4>
                    <ul className="text-blue-800 text-sm m-0 pl-5 space-y-1">
                        <li>Selected items will be added to your profile</li>
                        <li>You can edit or remove any imported items later</li>
                        <li>You'll be redirected to your profile management page after import</li>
                    </ul>
                </div>
            )}
        </div>
    );
};

export default ImportReviewStep;
